function _define_property(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _object_spread(target) {
    for(var i = 1; i < arguments.length; i++){
        var source = arguments[i] != null ? arguments[i] : {};
        var ownKeys = Object.keys(source);
        if (typeof Object.getOwnPropertySymbols === "function") {
            ownKeys = ownKeys.concat(Object.getOwnPropertySymbols(source).filter(function(sym) {
                return Object.getOwnPropertyDescriptor(source, sym).enumerable;
            }));
        }
        ownKeys.forEach(function(key) {
            _define_property(target, key, source[key]);
        });
    }
    return target;
}
function ownKeys(object, enumerableOnly) {
    var keys = Object.keys(object);
    if (Object.getOwnPropertySymbols) {
        var symbols = Object.getOwnPropertySymbols(object);
        if (enumerableOnly) {
            symbols = symbols.filter(function(sym) {
                return Object.getOwnPropertyDescriptor(object, sym).enumerable;
            });
        }
        keys.push.apply(keys, symbols);
    }
    return keys;
}
function _object_spread_props(target, source) {
    source = source != null ? source : {};
    if (Object.getOwnPropertyDescriptors) {
        Object.defineProperties(target, Object.getOwnPropertyDescriptors(source));
    } else {
        ownKeys(Object(source)).forEach(function(key) {
            Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key));
        });
    }
    return target;
}
function _object_without_properties(source, excluded) {
    if (source == null) return {};
    var target = _object_without_properties_loose(source, excluded);
    var key, i;
    if (Object.getOwnPropertySymbols) {
        var sourceSymbolKeys = Object.getOwnPropertySymbols(source);
        for(i = 0; i < sourceSymbolKeys.length; i++){
            key = sourceSymbolKeys[i];
            if (excluded.indexOf(key) >= 0) continue;
            if (!Object.prototype.propertyIsEnumerable.call(source, key)) continue;
            target[key] = source[key];
        }
    }
    return target;
}
function _object_without_properties_loose(source, excluded) {
    if (source == null) return {};
    var target = {};
    var sourceKeys = Object.keys(source);
    var key, i;
    for(i = 0; i < sourceKeys.length; i++){
        key = sourceKeys[i];
        if (excluded.indexOf(key) >= 0) continue;
        target[key] = source[key];
    }
    return target;
}
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { forwardRef, Fragment } from "react";
import { cva } from "class-variance-authority";
import { cn } from "../../common/helpers.js";
import { percentInteger } from "../../common/numbers.js";
import { Container as ContainerComponent } from "../Container/index.js";
import { Typography } from "../Typography/index.js";
import { Button } from "../Button/index.js";
const classesRoot = cva([
    'relative',
    'bg-gray-50',
    'lg:bg-none'
]);
const classesImageContainer = cva([], {
    variants: {
        size: {
            md: [
                'h-[300px]',
                'lg:h-[400px]'
            ],
            lg: [
                'h-[300px]',
                'lg:h-[545px]'
            ]
        }
    }
});
const classesText = cva([], {
    variants: {
        variant: {
            light: [
                'text-gray-700'
            ],
            dark: [
                'text-gray-700',
                'lg:text-white',
                'lg:[&_a]:!text-white'
            ]
        }
    }
});
const variantButtonColorMap = new Map([
    [
        'dark',
        'light'
    ],
    [
        'light',
        'primary'
    ]
]);
/**
 * Hero Block - [API](https://scl.sharp.com/?path=/docs/components-heroblock) | [DSM](https://sharphealthcare.notion.site/Hero-5a6c8bda786c43c784c8fb15c6b2828d)
 *
 * @example
 * <HeroBlock
 *  heading="Hello"
 *  image={{ src: 'image.jpg' }}
 * />
 */ const HeroBlock = /*#__PURE__*/ forwardRef((props, ref)=>{
    var _slotProps_heading, _slotProps_preHeading, _slotProps_description;
    const { preHeading, heading, description, buttonPrimary, buttonSecondary, image = {}, imageOverlayOpacity = 50, imageFocalPoint, variant = 'dark', size = 'lg', slots = {}, slotProps = {}, as, className } = props, rest = _object_without_properties(props, [
        "preHeading",
        "heading",
        "description",
        "buttonPrimary",
        "buttonSecondary",
        "image",
        "imageOverlayOpacity",
        "imageFocalPoint",
        "variant",
        "size",
        "slots",
        "slotProps",
        "as",
        "className"
    ]);
    const textClasses = classesText({
        variant
    });
    const Root = as !== null && as !== void 0 ? as : 'div';
    const Container = ContainerComponent;
    var _slots_contentContainer;
    const ContentContainer = (_slots_contentContainer = slots.contentContainer) !== null && _slots_contentContainer !== void 0 ? _slots_contentContainer : 'div';
    var _slotProps_contentContainer;
    const _ref = (_slotProps_contentContainer = slotProps.contentContainer) !== null && _slotProps_contentContainer !== void 0 ? _slotProps_contentContainer : {}, { className: contentContainerSlotPropsClassName } = _ref, contentContainerSlotProps = _object_without_properties(_ref, [
        "className"
    ]);
    var _slots_preHeading;
    const PreHeading = (_slots_preHeading = slots.preHeading) !== null && _slots_preHeading !== void 0 ? _slots_preHeading : Typography;
    var _slotProps_preHeading1;
    const _ref1 = (_slotProps_preHeading1 = slotProps.preHeading) !== null && _slotProps_preHeading1 !== void 0 ? _slotProps_preHeading1 : {}, { className: preHeadingSlotPropsClassName } = _ref1, preHeadingSlotProps = _object_without_properties(_ref1, [
        "className"
    ]);
    // If preHeading slot provided then do not spread Typography specific props else combine
    const preHeadingProps = slots.preHeading ? preHeadingSlotProps : _object_spread({
        variant: 'small-body-semibold'
    }, preHeadingSlotProps);
    var _slots_heading;
    const Heading = (_slots_heading = slots.heading) !== null && _slots_heading !== void 0 ? _slots_heading : Typography;
    var _slotProps_heading1;
    const _ref2 = (_slotProps_heading1 = slotProps.heading) !== null && _slotProps_heading1 !== void 0 ? _slotProps_heading1 : {}, { className: headingSlotPropsClassName } = _ref2, headingSlotProps = _object_without_properties(_ref2, [
        "className"
    ]);
    // If heading slot provided then do not spread Typography specific props else combine
    const headingProps = slots.heading ? headingSlotProps : _object_spread({
        variant: 'h1'
    }, headingSlotProps);
    var _slots_description;
    const Description = (_slots_description = slots.description) !== null && _slots_description !== void 0 ? _slots_description : Typography;
    var _slotProps_description1;
    const _ref3 = (_slotProps_description1 = slotProps.description) !== null && _slotProps_description1 !== void 0 ? _slotProps_description1 : {}, { className: descriptionSlotPropsClassName } = _ref3, descriptionSlotProps = _object_without_properties(_ref3, [
        "className"
    ]);
    // If description slot provided then do not spread Typography specific props else combine
    const descriptionProps = slots.description ? descriptionSlotProps : _object_spread({
        variant: 'body',
        as: 'div'
    }, descriptionSlotProps);
    var _slots_buttonContainer;
    const ButtonContainer = (_slots_buttonContainer = slots.buttonContainer) !== null && _slots_buttonContainer !== void 0 ? _slots_buttonContainer : 'div';
    var _slotProps_buttonContainer;
    const _ref4 = (_slotProps_buttonContainer = slotProps.buttonContainer) !== null && _slotProps_buttonContainer !== void 0 ? _slotProps_buttonContainer : {}, { className: buttonContainerSlotPropsClassName } = _ref4, buttonContainerSlotProps = _object_without_properties(_ref4, [
        "className"
    ]);
    var _slots_buttonPrimary, _ref5;
    const hasButtonPrimary = !!((_ref5 = (_slots_buttonPrimary = slots.buttonPrimary) !== null && _slots_buttonPrimary !== void 0 ? _slots_buttonPrimary : slotProps.buttonPrimary) !== null && _ref5 !== void 0 ? _ref5 : buttonPrimary);
    var _slots_buttonSecondary, _ref6;
    const hasButtonSecondary = !!((_ref6 = (_slots_buttonSecondary = slots.buttonSecondary) !== null && _slots_buttonSecondary !== void 0 ? _slots_buttonSecondary : slotProps.buttonSecondary) !== null && _ref6 !== void 0 ? _ref6 : buttonSecondary);
    const hasOneButton = hasButtonPrimary || hasButtonSecondary;
    const hasTwoButtons = hasButtonPrimary && hasButtonSecondary;
    const hasButton = hasOneButton;
    const buttonWidthProp = hasTwoButtons ? 'md' : 'lg';
    const buttonColorProp = variantButtonColorMap.get(variant);
    var _slots_buttonPrimary1;
    const ButtonPrimary = (_slots_buttonPrimary1 = slots.buttonPrimary) !== null && _slots_buttonPrimary1 !== void 0 ? _slots_buttonPrimary1 : Button;
    var _slotProps_buttonPrimary;
    const _ref7 = (_slotProps_buttonPrimary = slotProps.buttonPrimary) !== null && _slotProps_buttonPrimary !== void 0 ? _slotProps_buttonPrimary : {}, { className: buttonPrimarySlotPropsClassName } = _ref7, buttonPrimarySlotProps = _object_without_properties(_ref7, [
        "className"
    ]);
    const buttonPrimaryDefaultProps = {
        color: buttonColorProp,
        variant: 'filled',
        width: buttonWidthProp
    };
    const buttonPrimaryDefaultMobileProps = {
        color: 'primary',
        variant: 'filled',
        width: buttonWidthProp
    };
    const buttonPrimaryBaseProps = _object_spread({}, buttonPrimaryDefaultProps, buttonPrimary, buttonPrimarySlotProps);
    // If buttonPrimary slot provided then do not spread Button specific props else combine
    const buttonPrimaryProps = slots.buttonPrimary ? buttonPrimarySlotProps : buttonPrimaryBaseProps;
    const buttonPrimaryMobileProps = slots.buttonPrimary ? buttonPrimarySlotProps : _object_spread({}, buttonPrimaryBaseProps, buttonPrimaryDefaultMobileProps);
    var _slots_buttonSecondary1;
    const ButtonSecondary = (_slots_buttonSecondary1 = slots.buttonSecondary) !== null && _slots_buttonSecondary1 !== void 0 ? _slots_buttonSecondary1 : Button;
    var _slotProps_buttonSecondary;
    const _ref8 = (_slotProps_buttonSecondary = slotProps.buttonSecondary) !== null && _slotProps_buttonSecondary !== void 0 ? _slotProps_buttonSecondary : {}, { className: buttonSecondarySlotPropsClassName } = _ref8, buttonSecondarySlotProps = _object_without_properties(_ref8, [
        "className"
    ]);
    const buttonSecondaryDefaultProps = {
        color: buttonColorProp,
        variant: 'outlined',
        width: buttonWidthProp
    };
    const buttonSecondaryDefaultMobileProps = {
        color: 'primary',
        variant: 'outlined',
        width: buttonWidthProp
    };
    const buttonSecondaryBaseProps = _object_spread({}, buttonSecondaryDefaultProps, buttonSecondary, buttonSecondarySlotProps);
    // If buttonSecondary slot provided then do not spread Button specific props else combine
    const buttonSecondaryProps = slots.buttonSecondary ? buttonSecondarySlotProps : buttonSecondaryBaseProps;
    const buttonSecondaryMobileProps = slots.buttonSecondary ? buttonSecondarySlotProps : _object_spread({}, buttonSecondaryBaseProps, buttonSecondaryDefaultMobileProps);
    var _slots_buttonPrimaryContainer;
    const ButtonPrimaryContainer = (_slots_buttonPrimaryContainer = slots.buttonPrimaryContainer) !== null && _slots_buttonPrimaryContainer !== void 0 ? _slots_buttonPrimaryContainer : slotProps.buttonPrimaryContainer ? 'div' : Fragment;
    var _slots_buttonSecondaryContainer;
    const ButtonSecondaryContainer = (_slots_buttonSecondaryContainer = slots.buttonSecondaryContainer) !== null && _slots_buttonSecondaryContainer !== void 0 ? _slots_buttonSecondaryContainer : slotProps.buttonSecondaryContainer ? 'div' : Fragment;
    var _slots_imageContainer;
    const ImageContainer = (_slots_imageContainer = slots.imageContainer) !== null && _slots_imageContainer !== void 0 ? _slots_imageContainer : 'div';
    var _slotProps_imageContainer;
    const _ref9 = (_slotProps_imageContainer = slotProps.imageContainer) !== null && _slotProps_imageContainer !== void 0 ? _slotProps_imageContainer : {}, { className: imageContainerSlotPropsClassName } = _ref9, imageContainerSlotProps = _object_without_properties(_ref9, [
        "className"
    ]);
    const focalPoint = Array.isArray(imageFocalPoint) ? imageFocalPoint.map(percentInteger) : [
        50,
        50
    ];
    const [fpLeft = 50, fpTop = 50] = focalPoint;
    // Shift focal point 33% to meet 'rule of thirds'
    const fpLeftLarge = Math.max(fpLeft - 33, 0);
    var _slots_image;
    const Image = (_slots_image = slots.image) !== null && _slots_image !== void 0 ? _slots_image : 'img';
    const { className: imagePropClassName } = image, imagePropProps = _object_without_properties(image, [
        "className"
    ]);
    var _slotProps_image;
    const _ref10 = (_slotProps_image = slotProps.image) !== null && _slotProps_image !== void 0 ? _slotProps_image : {}, { className: imageSlotPropsClassName } = _ref10, imageSlotProps = _object_without_properties(_ref10, [
        "className"
    ]);
    const imageProps = slots.image ? _object_spread({}, imageSlotProps) : _object_spread({}, imagePropProps, imageSlotProps);
    const imageOverlayStyle = {
        background: `linear-gradient(90deg, rgba(0, 0, 0, ${percentInteger(imageOverlayOpacity)}%) 0%, rgba(0, 0, 0, 0) 100%)`
    };
    var _slots_imageOverlay;
    const ImageOverlay = (_slots_imageOverlay = slots.imageOverlay) !== null && _slots_imageOverlay !== void 0 ? _slots_imageOverlay : 'div';
    var _slotProps_imageOverlay;
    const _ref11 = (_slotProps_imageOverlay = slotProps.imageOverlay) !== null && _slotProps_imageOverlay !== void 0 ? _slotProps_imageOverlay : {}, { className: imageOverlaySlotPropsClassName, style: imageOverlaySlotPropsStyle } = _ref11, imageOverlayProps = _object_without_properties(_ref11, [
        "className",
        "style"
    ]);
    return /*#__PURE__*/ _jsxs(Root, _object_spread_props(_object_spread({
        className: cn(classesRoot(), className)
    }, rest), {
        ref: ref,
        children: [
            /*#__PURE__*/ _jsxs(ImageContainer, _object_spread_props(_object_spread({
                className: cn('relative block overflow-hidden', classesImageContainer({
                    size
                }), imageContainerSlotPropsClassName)
            }, imageContainerSlotProps), {
                children: [
                    /*#__PURE__*/ _jsx("style", {
                        children: `
            .scl-hero-block-image {
              object-position: left ${fpLeft}% top ${fpTop}%;
            }
            @media (min-width: 1024px) {
              .scl-hero-block-image {
                object-position: left ${fpLeftLarge}% top ${fpTop}%;
              }
            }
          `
                    }),
                    /*#__PURE__*/ _jsx(Image, _object_spread({
                        className: cn('scl-hero-block-image object-cover w-full h-full absolute inset-0 text-transparent', imagePropClassName, imageSlotPropsClassName)
                    }, imageProps))
                ]
            })),
            /*#__PURE__*/ _jsx(ImageOverlay, _object_spread({
                className: cn('hidden lg:block lg:absolute lg:top-0 lg:left-0 lg:right-0 lg:bottom-0', imageOverlaySlotPropsClassName),
                style: _object_spread({}, imageOverlayStyle, imageOverlaySlotPropsStyle !== null && imageOverlaySlotPropsStyle !== void 0 ? imageOverlaySlotPropsStyle : {})
            }, imageOverlayProps)),
            (heading !== null && heading !== void 0 ? heading : (_slotProps_heading = slotProps.heading) === null || _slotProps_heading === void 0 ? void 0 : _slotProps_heading.children) && /*#__PURE__*/ _jsx(Container, {
                className: "lg:block lg:absolute lg:top-0 lg:left-0 lg:right-0 lg:bottom-0",
                children: /*#__PURE__*/ _jsxs(ContentContainer, _object_spread_props(_object_spread({
                    className: cn('flex flex-col content-center justify-center h-full', 'py-5 md:py-6 lg:py-0 gap-3 lg:gap-5', contentContainerSlotPropsClassName)
                }, contentContainerSlotProps), {
                    children: [
                        (preHeading !== null && preHeading !== void 0 ? preHeading : (_slotProps_preHeading = slotProps.preHeading) === null || _slotProps_preHeading === void 0 ? void 0 : _slotProps_preHeading.children) && /*#__PURE__*/ _jsx(PreHeading, _object_spread_props(_object_spread({
                            className: cn('line-clamp-1 w-full lg:w-5/12', textClasses, preHeadingSlotPropsClassName)
                        }, preHeadingProps), {
                            children: preHeading
                        })),
                        /*#__PURE__*/ _jsx(Heading, _object_spread_props(_object_spread({
                            className: cn('line-clamp-3 md:line-clamp-2 w-full lg:w-1/2 xl:w-5/12', textClasses, headingSlotPropsClassName)
                        }, headingProps), {
                            children: heading
                        })),
                        (description !== null && description !== void 0 ? description : (_slotProps_description = slotProps.description) === null || _slotProps_description === void 0 ? void 0 : _slotProps_description.children) && /*#__PURE__*/ _jsx(Description, _object_spread_props(_object_spread({
                            className: cn('scl-rich-text w-full lg:w-1/2 xl:w-5/12 ', textClasses, descriptionSlotPropsClassName)
                        }, descriptionProps), {
                            children: description
                        })),
                        hasButton && /*#__PURE__*/ _jsxs(ButtonContainer, _object_spread_props(_object_spread({
                            className: cn('flex flex-col gap-5 mt-2 w-full sm:flex-row sm:flex-wrap lg:mt-5 lg:w-1/2 xl:w-5/12', buttonContainerSlotPropsClassName)
                        }, buttonContainerSlotProps), {
                            children: [
                                hasButtonPrimary && /*#__PURE__*/ _jsxs(ButtonPrimaryContainer, _object_spread_props(_object_spread({}, slotProps.buttonPrimaryContainer), {
                                    children: [
                                        /*#__PURE__*/ _jsx(ButtonPrimary, _object_spread({
                                            className: cn('hidden lg:flex', buttonPrimarySlotPropsClassName)
                                        }, buttonPrimaryProps)),
                                        /*#__PURE__*/ _jsx(ButtonPrimary, _object_spread({
                                            className: cn('flex lg:hidden', buttonPrimarySlotPropsClassName)
                                        }, buttonPrimaryMobileProps))
                                    ]
                                })),
                                hasButtonSecondary && /*#__PURE__*/ _jsxs(ButtonSecondaryContainer, _object_spread_props(_object_spread({}, slotProps.buttonSecondaryContainer), {
                                    children: [
                                        /*#__PURE__*/ _jsx(ButtonSecondary, _object_spread({
                                            className: cn('hidden lg:flex', buttonSecondarySlotPropsClassName)
                                        }, buttonSecondaryProps)),
                                        /*#__PURE__*/ _jsx(ButtonSecondary, _object_spread({
                                            className: cn('flex lg:hidden', buttonSecondarySlotPropsClassName)
                                        }, buttonSecondaryMobileProps))
                                    ]
                                }))
                            ]
                        }))
                    ]
                }))
            })
        ]
    }));
});
export { HeroBlock };
